import { PropsWithChildren } from 'react';
import { Stack } from 'src/core/ui';
import { useMapDeliveryTypesToString } from 'src/hooks/useMapDeliveryTypesToString';
import { FormatOption } from 'src/modals/EditTableColumns';
import { CheckCircleIcon } from 'src/svgs/CheckCircleIcon';
import { getDeliveryTypeIcon } from 'src/utils/ticketTypeUtils';
import {
  DeliveryType,
  Listing,
  ListingGroup,
  PreDeliveryState,
  TicketType,
} from 'src/WebApiController';

import * as styles from './ListingDeliveryTypeDisplay.css';

type DeliveryTypeIconWrapperProps = PropsWithChildren & {
  addCheckIcon: boolean;
  addWarningDot?: boolean;
  iconContainerClassName?: string;
};
const DeliveryTypeIconWrapper = ({
  addCheckIcon,
  addWarningDot,
  iconContainerClassName,
  children,
}: DeliveryTypeIconWrapperProps) => {
  if (iconContainerClassName) {
    return <div className={iconContainerClassName}>{children}</div>;
  }
  if (addCheckIcon) {
    return (
      <div style={{ position: 'relative' }}>
        {children}
        <CheckCircleIcon className={styles.checkIcon} size="11px" />
      </div>
    );
  }
  if (addWarningDot) {
    return (
      <div style={{ position: 'relative' }}>
        {children}
        <div className={styles.warningDot} />
      </div>
    );
  }
  return <>{children}</>;
};

const DeliveryTypeTextWrapper = ({
  addCheckIcon,
  addWarningDot,
  children,
}: DeliveryTypeIconWrapperProps) => {
  if (addCheckIcon) {
    return (
      <Stack gap="s" alignItems="center">
        {children}
        <CheckCircleIcon className={styles.checkIconForText} size="11px" />
      </Stack>
    );
  }
  if (addWarningDot) {
    return (
      <Stack gap="s" alignItems="center">
        {children}
        <div className={styles.warningDotForText} />
      </Stack>
    );
  }
  return <>{children}</>;
};

export const ListingDeliveryTypeDisplay = ({
  listing,
  userDefinedPrecision,
  showIconTitle,
  iconContainerClassName,
}: {
  listing:
    | Listing
    | {
        delivType: DeliveryType;
        topPriTktType?: TicketType;
        preDelivState?: PreDeliveryState;
      };
  userDefinedPrecision: FormatOption;
  showIconTitle?: boolean;
  iconContainerClassName?: string;
}) => {
  const listingGroup = listing as ListingGroup;

  const strDeliveryTypes = useMapDeliveryTypesToString({
    deliveryTypesObj: listing,
  });

  const shouldAddCheckIcon =
    listing.preDelivState === PreDeliveryState.PreDelivered;

  const shouldAddWarningDot =
    listing.preDelivState === PreDeliveryState.PartiallyPreDelivered;

  const isSingleListingArtifactUploadType =
    listing.topPriTktType === TicketType.Barcode ||
    listing.topPriTktType === TicketType.ETicket ||
    listing.topPriTktType === TicketType.QRCode ||
    listing.topPriTktType === TicketType.ETicketUrl;

  return (
    <Stack alignItems="center">
      {listingGroup.deliveryTypes ? (
        <div className={styles.subRowsMetadataContainer}>
          <Stack direction="row" gap="s">
            {listingGroup.deliveryTypes.map((dt) => {
              const key = `${dt.item1}-${dt.item2}`;
              const isArtifactUploadType =
                dt.item2 === TicketType.Barcode ||
                dt.item2 === TicketType.ETicket ||
                dt.item2 === TicketType.QRCode ||
                dt.item2 === TicketType.ETicketUrl;
              if (userDefinedPrecision === FormatOption.EnumDisplay_Icon) {
                return (
                  <DeliveryTypeIconWrapper
                    key={key}
                    iconContainerClassName={iconContainerClassName}
                    addCheckIcon={shouldAddCheckIcon}
                    addWarningDot={shouldAddWarningDot && isArtifactUploadType}
                  >
                    {getDeliveryTypeIcon(
                      dt.item1,
                      dt.item2,
                      undefined,
                      showIconTitle
                    )}
                  </DeliveryTypeIconWrapper>
                );
              } else {
                return (
                  <DeliveryTypeTextWrapper
                    addCheckIcon={shouldAddCheckIcon}
                    addWarningDot={shouldAddWarningDot && isArtifactUploadType}
                    key={key}
                  >
                    {strDeliveryTypes}
                  </DeliveryTypeTextWrapper>
                );
              }
            })}
          </Stack>
        </div>
      ) : (
        <>
          {userDefinedPrecision === FormatOption.EnumDisplay_Icon ? (
            <DeliveryTypeIconWrapper
              iconContainerClassName={iconContainerClassName}
              addCheckIcon={shouldAddCheckIcon}
              addWarningDot={
                shouldAddWarningDot && isSingleListingArtifactUploadType
              }
            >
              {getDeliveryTypeIcon(
                listing.delivType,
                listing.topPriTktType,
                undefined,
                showIconTitle
              )}
            </DeliveryTypeIconWrapper>
          ) : (
            <DeliveryTypeTextWrapper
              addCheckIcon={shouldAddCheckIcon}
              addWarningDot={
                shouldAddWarningDot && isSingleListingArtifactUploadType
              }
            >
              {strDeliveryTypes}
            </DeliveryTypeTextWrapper>
          )}
        </>
      )}
    </Stack>
  );
};
